/**
 * Upload Queue Manager
 * Handles file grouping, queue management, progress tracking, and speed calculation
 */

import api from '@/utils/api';
import { UploadFile } from 'antd';

// File size thresholds (in bytes)
const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024; // 5MB
const MEDIUM_FILE_THRESHOLD = 1 * 1024 * 1024; // 1MB

// Upload task status
export type UploadTaskStatus =
  | 'pending'
  | 'uploading'
  | 'success'
  | 'skipped'
  | 'error'
  | 'rejected'
  | 'cancelled'
  | 'retrying';

// Upload task interface
export interface UploadTask {
  fileId: string; // Frontend generated unique ID
  file: File;
  groupId?: string; // Group ID (for batch upload)
  status: UploadTaskStatus;
  progress: number; // 0-100
  uploadedBytes: number;
  totalBytes: number;
  speed: number; // MB/s
  error?: string;
  errorCode?: string;
  causeCode?: string;
  messageKey?: string;
  declaredFormat?: string;
  detectedFormat?: string;
  retryable?: boolean;
  documentId?: string; // Backend returned document ID
  parseRequested?: boolean;
  parseEnqueued?: boolean | null;
  retryCount?: number; // Retry count
}

// File group interface
export interface FileGroup {
  groupId: string;
  files: File[];
  totalSize: number;
}

// Upload queue state
export interface UploadQueueState {
  tasks: UploadTask[];
  totalFiles: number;
  uploadedFiles: number;
  totalBytes: number;
  uploadedBytes: number;
  overallProgress: number; // Based on file count
  sizeProgress: number; // Based on file size
  averageSpeed: number; // MB/s
  isUploading: boolean;
  successCount: number;
  skippedCount: number;
  failedCount: number;
}

// Speed calculator (moving average over time window)
class SpeedCalculator {
  private samples: Array<{ time: number; bytes: number }> = [];
  private windowSize = 3000; // 3 seconds

  update(bytes: number): number {
    const now = Date.now();
    this.samples.push({ time: now, bytes });

    // Remove samples outside time window
    this.samples = this.samples.filter((s) => now - s.time <= this.windowSize);

    if (this.samples.length < 2) return 0;

    const oldest = this.samples[0];
    const newest = this.samples[this.samples.length - 1];
    const timeDiff = (newest.time - oldest.time) / 1000; // seconds
    const bytesDiff = newest.bytes - oldest.bytes;

    return bytesDiff / timeDiff / 1024 / 1024; // MB/s
  }

  reset() {
    this.samples = [];
  }
}

// Upload queue class
export class UploadQueue {
  private tasks: UploadTask[] = [];
  private concurrentLimit: number = 3;
  private activeUploads: number = 0;
  private onProgressCallback?: (state: UploadQueueState) => void;
  private abortControllers: Map<string, XMLHttpRequest> = new Map();
  private speedCalculators: Map<string, SpeedCalculator> = new Map();
  private isPaused: boolean = false;
  private isCancelled: boolean = false;
  private maxRetries: number = 3;
  private retryDelay: number = 1000; // 1 second

  // Configuration
  private kb_id: string;
  private parse_on_creation: boolean;
  private meta_fields?: any;

  constructor(
    kb_id: string,
    options?: {
      concurrentLimit?: number;
      parse_on_creation?: boolean;
      meta_fields?: any;
      onProgress?: (state: UploadQueueState) => void;
    },
  ) {
    this.kb_id = kb_id;
    this.concurrentLimit = options?.concurrentLimit || 3;
    this.parse_on_creation = options?.parse_on_creation || false;
    this.meta_fields = options?.meta_fields;
    this.onProgressCallback = options?.onProgress;
  }

  /**
   * Group files based on size strategy
   */
  groupFiles(files: File[]): FileGroup[] {
    const groups: FileGroup[] = [];
    let currentGroup: File[] = [];
    let currentGroupSize = 0;

    for (const file of files) {
      if (file.size > LARGE_FILE_THRESHOLD) {
        // Large file (>5MB): upload alone
        if (currentGroup.length > 0) {
          groups.push({
            groupId: this.generateGroupId(),
            files: currentGroup,
            totalSize: currentGroupSize,
          });
          currentGroup = [];
          currentGroupSize = 0;
        }
        groups.push({
          groupId: this.generateGroupId(),
          files: [file],
          totalSize: file.size,
        });
      } else if (file.size > MEDIUM_FILE_THRESHOLD) {
        // Medium file (1-5MB): max 2 files per group
        if (currentGroup.length >= 2) {
          groups.push({
            groupId: this.generateGroupId(),
            files: currentGroup,
            totalSize: currentGroupSize,
          });
          currentGroup = [];
          currentGroupSize = 0;
        }
        currentGroup.push(file);
        currentGroupSize += file.size;
      } else {
        // Small file (<1MB): max 5 files per group
        if (currentGroup.length >= 5) {
          groups.push({
            groupId: this.generateGroupId(),
            files: currentGroup,
            totalSize: currentGroupSize,
          });
          currentGroup = [];
          currentGroupSize = 0;
        }
        currentGroup.push(file);
        currentGroupSize += file.size;
      }
    }

    if (currentGroup.length > 0) {
      groups.push({
        groupId: this.generateGroupId(),
        files: currentGroup,
        totalSize: currentGroupSize,
      });
    }

    return groups;
  }

  /**
   * Add tasks to queue
   */
  addTasks(files: UploadFile[]): void {
    const newTasks: UploadTask[] = files
      .map((uploadFile) => {
        // Get the actual File object
        // UploadFile can have originFileObj or be a File itself
        let file: File | null = null;

        if (uploadFile.originFileObj) {
          file = uploadFile.originFileObj as File;
        } else if ((uploadFile as any) instanceof File) {
          file = uploadFile as any as File;
        } else if ((uploadFile as any).size !== undefined) {
          // It's already a File-like object
          file = uploadFile as any as File;
        }

        if (!file) {
          console.warn('Invalid file object:', uploadFile);
          return null;
        }

        return {
          fileId: this.generateFileId(),
          file,
          status: 'pending' as UploadTaskStatus,
          progress: 0,
          uploadedBytes: 0,
          totalBytes: file.size,
          speed: 0,
          retryCount: 0,
        };
      })
      .filter((task): task is UploadTask => task !== null);

    this.tasks = [...this.tasks, ...newTasks];
    this.notifyProgress();
  }

  /**
   * Start upload
   */
  async start(): Promise<void> {
    this.isPaused = false;
    this.isCancelled = false;

    // Group files
    const filesToUpload = this.tasks
      .filter((t) => t.status === 'pending')
      .map((t) => t.file);

    const groups = this.groupFiles(filesToUpload);

    // Assign groupId to tasks
    for (const group of groups) {
      for (const file of group.files) {
        const task = this.tasks.find(
          (t) => t.file === file && t.status === 'pending',
        );
        if (task) {
          task.groupId = group.groupId;
        }
      }
    }

    // Start uploading groups with concurrency control
    const uploadPromises: Promise<void>[] = [];
    for (const group of groups) {
      if (this.isCancelled) break;

      // Wait if concurrent limit reached
      while (this.activeUploads >= this.concurrentLimit && !this.isCancelled) {
        await this.sleep(100);
      }

      if (this.isCancelled) break;

      uploadPromises.push(this.uploadGroup(group));
    }

    await Promise.all(uploadPromises);
    this.notifyProgress();
  }

  /**
   * Upload file group
   */
  private async uploadGroup(group: FileGroup): Promise<void> {
    this.activeUploads++;

    try {
      // Find tasks in this group
      const groupTasks = this.tasks.filter((t) => t.groupId === group.groupId);

      // Update status to uploading
      groupTasks.forEach((t) => {
        t.status = 'uploading';
      });
      this.notifyProgress();

      // Create FormData
      const formData = new FormData();
      formData.append('kb_id', this.kb_id);
      formData.append('parse_on_creation', this.parse_on_creation.toString());

      // Add files with their fileIds for proper matching
      const fileIds: string[] = [];
      for (const task of groupTasks) {
        formData.append('file', task.file);
        fileIds.push(task.fileId);
      }

      // Add fileIds array so backend can return them
      formData.append('file_ids', JSON.stringify(fileIds));

      // Add metadata if provided
      if (this.meta_fields) {
        if (Array.isArray(this.meta_fields)) {
          // Array of metadata (one per file)
          formData.append('meta', JSON.stringify(this.meta_fields));
        } else {
          // Single metadata object (applied to all files)
          formData.append('meta', JSON.stringify(this.meta_fields));
        }
      }

      // Upload with XMLHttpRequest
      const result = await this.uploadWithProgress(
        formData,
        group.groupId,
        group.totalSize,
        (progress, speed) => {
          // Create new task refs (not mutate) so React.memo detects changes
          groupTasks.forEach((t) => {
            const idx = this.tasks.findIndex((x) => x.fileId === t.fileId);
            if (idx >= 0) {
              this.tasks[idx] = {
                ...t,
                progress,
                speed,
                uploadedBytes: (progress / 100) * t.totalBytes,
              };
            }
          });
          this.notifyProgress();
        },
      );

      // Handle response (must update this.tasks, not groupTasks - progress may have replaced refs)
      if (result.code === 0 && result.data) {
        const successFiles = result.data.success || [];
        const failedFiles = result.data.failed || [];
        const parseOutcomeFiles = [
          ...(result.data.parse_failed || []),
          ...(result.data.parse_uncertain || []),
        ];
        const duplicateFileIds = result.data.duplicate_file_ids || [];

        for (const successFile of successFiles) {
          const fileId = successFile.file_id;
          const name = successFile.name;
          const idx = this.tasks.findIndex(
            (t) =>
              (fileId && t.fileId === fileId) ||
              (!fileId && t.file.name === name),
          );
          if (idx >= 0) {
            const t = this.tasks[idx];
            this.tasks[idx] = {
              ...t,
              status: 'success',
              progress: 100,
              uploadedBytes: t.totalBytes,
              speed: 0,
              documentId: successFile.id,
              parseRequested: successFile.parse_requested,
              parseEnqueued: successFile.parse_enqueued,
            };
          }
        }

        for (const failedFile of failedFiles) {
          const fileId = failedFile.file_id;
          const name = failedFile.name;
          const idx = this.tasks.findIndex(
            (t) =>
              (fileId && t.fileId === fileId) ||
              (!fileId && t.file.name === name),
          );
          if (idx >= 0) {
            const t = this.tasks[idx];
            const err = failedFile.error || 'Unknown error';
            const retryable = failedFile.retryable !== false;
            const uploadSucceeded = failedFile.upload_succeeded === true;
            this.tasks[idx] = {
              ...t,
              status: uploadSucceeded
                ? 'success'
                : retryable
                  ? 'error'
                  : 'rejected',
              progress: uploadSucceeded ? 100 : t.progress,
              uploadedBytes: uploadSucceeded ? t.totalBytes : t.uploadedBytes,
              error: err,
              errorCode: failedFile.error_code,
              causeCode: failedFile.cause_code,
              messageKey: failedFile.message_key,
              declaredFormat: failedFile.declared_format,
              detectedFormat: failedFile.detected_format,
              retryable,
              documentId: failedFile.document_id || t.documentId,
              parseRequested: uploadSucceeded
                ? failedFile.parse_requested
                : t.parseRequested,
              parseEnqueued: uploadSucceeded
                ? failedFile.parse_enqueued
                : t.parseEnqueued,
              speed: 0,
            };
          }
        }

        for (const parseOutcome of parseOutcomeFiles) {
          const fileId = parseOutcome.file_id;
          const name = parseOutcome.name;
          const idx = this.tasks.findIndex(
            (t) =>
              (fileId && t.fileId === fileId) ||
              (!fileId && t.file.name === name),
          );
          if (idx >= 0) {
            const t = this.tasks[idx];
            this.tasks[idx] = {
              ...t,
              status: 'success',
              progress: 100,
              uploadedBytes: t.totalBytes,
              error: parseOutcome.error || 'Unknown parse enqueue error',
              errorCode: parseOutcome.error_code,
              causeCode: parseOutcome.cause_code,
              retryable: parseOutcome.retryable !== false,
              documentId: parseOutcome.document_id || t.documentId,
              parseRequested: true,
              parseEnqueued: parseOutcome.parse_enqueued,
              speed: 0,
            };
          }
        }

        for (const fileId of duplicateFileIds) {
          const idx = this.tasks.findIndex((t) => t.fileId === fileId);
          if (idx >= 0) {
            const t = this.tasks[idx];
            this.tasks[idx] = {
              ...t,
              status: 'skipped',
              progress: 100,
              uploadedBytes: t.totalBytes,
              error: undefined,
              speed: 0,
            };
          }
        }
      } else {
        // All tasks in group failed
        const msg = result.message || 'Upload failed';
        for (let i = 0; i < this.tasks.length; i++) {
          if (this.tasks[i].groupId === group.groupId) {
            this.tasks[i] = {
              ...this.tasks[i],
              status: 'error',
              error: msg,
              speed: 0,
            };
          }
        }
      }

      // Critical: Always notify progress after status update
      this.notifyProgress();
    } catch (error: any) {
      console.error('[UploadQueue] uploadGroup error:', error);
      const isCancelled = error.message === 'Upload cancelled';
      const errMsg = isCancelled
        ? 'Cancelled by user'
        : error.message || 'Network error';
      const newStatus = isCancelled ? 'cancelled' : 'error';
      for (let i = 0; i < this.tasks.length; i++) {
        if (this.tasks[i].groupId === group.groupId) {
          this.tasks[i] = {
            ...this.tasks[i],
            status: newStatus,
            error: errMsg,
            speed: 0,
          };
        }
      }
      this.notifyProgress();
    } finally {
      this.activeUploads--;
      this.speedCalculators.delete(group.groupId);
    }
  }

  /**
   * Upload with progress tracking using XMLHttpRequest
   */
  private uploadWithProgress(
    formData: FormData,
    groupId: string,
    totalSize: number,
    onProgress: (progress: number, speed: number) => void,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.abortControllers.set(groupId, xhr);

      const speedCalculator = new SpeedCalculator();
      this.speedCalculators.set(groupId, speedCalculator);

      // Progress event
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && e.total > 0) {
          const percent = (e.loaded / e.total) * 100;
          const speed = speedCalculator.update(e.loaded);
          onProgress(percent, speed);
        } else {
          // Fallback: no total size available
          const speed = speedCalculator.update(e.loaded);
          onProgress(-1, speed); // -1 indicates indeterminate progress
        }
      });

      // Load event (success)
      xhr.addEventListener('load', () => {
        this.abortControllers.delete(groupId);

        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            console.error(
              '[UploadQueue] Failed to parse response:',
              e,
              xhr.responseText,
            );
            reject(new Error('Invalid JSON response'));
          }
        } else {
          console.error(
            '[UploadQueue] HTTP error:',
            xhr.status,
            xhr.statusText,
          );
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      });

      // Error event
      xhr.addEventListener('error', (e) => {
        console.error('[UploadQueue] XHR error event:', e);
        this.abortControllers.delete(groupId);
        reject(new Error('Network error'));
      });

      // Abort event
      xhr.addEventListener('abort', () => {
        this.abortControllers.delete(groupId);
        reject(new Error('Upload cancelled'));
      });

      // Timeout event
      xhr.addEventListener('timeout', () => {
        console.error('[UploadQueue] XHR timeout');
        this.abortControllers.delete(groupId);
        reject(new Error('Upload timeout'));
      });

      // Open request first
      xhr.open('POST', api.document_upload_batch);

      // Remove timeout limit for large file uploads (previously 30 seconds)
      // xhr.timeout = 30000; // Removed to support large file uploads

      // Get auth token from localStorage (same as request.ts)
      const authorization = this.getAuthToken();
      if (authorization) {
        xhr.setRequestHeader('Authorization', authorization);
      }

      xhr.send(formData);
    });
  }

  /**
   * Cancel specific file
   */
  cancelFile(fileId: string): void {
    const idx = this.tasks.findIndex((t) => t.fileId === fileId);
    if (idx < 0) return;

    const task = this.tasks[idx];
    if (task.groupId) {
      const xhr = this.abortControllers.get(task.groupId);
      if (xhr) {
        xhr.abort();
        this.abortControllers.delete(task.groupId);
      }
    }

    this.tasks[idx] = {
      ...task,
      status: 'cancelled',
      error: 'Cancelled by user',
      speed: 0,
    };
    this.notifyProgress();
  }

  /**
   * Cancel all uploads
   */
  cancelAll(): void {
    this.isCancelled = true;

    // Abort all active requests
    this.abortControllers.forEach((xhr) => xhr.abort());
    this.abortControllers.clear();

    // Update all uploading/pending tasks (create new refs so React.memo detects)
    for (let i = 0; i < this.tasks.length; i++) {
      const t = this.tasks[i];
      if (t.status === 'uploading' || t.status === 'pending') {
        this.tasks[i] = {
          ...t,
          status: 'cancelled',
          error: 'Cancelled by user',
          speed: 0,
        };
      }
    }

    this.notifyProgress();
  }

  /**
   * Pause upload
   */
  pause(): void {
    this.isPaused = true;
  }

  /**
   * Resume upload
   */
  resume(): void {
    this.isPaused = false;
  }

  /**
   * Retry failed files
   */
  async retryFailed(): Promise<void> {
    // Reset failed tasks to pending (create new refs)
    for (let i = 0; i < this.tasks.length; i++) {
      const t = this.tasks[i];
      const retryAllowed =
        t.errorCode !== 'FILE_VALIDATION_TIMEOUT' || (t.retryCount || 0) < 1;
      if (t.status === 'error' && t.retryable !== false && retryAllowed) {
        this.tasks[i] = {
          ...t,
          status: 'pending',
          progress: 0,
          uploadedBytes: 0,
          error: undefined,
          errorCode: undefined,
          messageKey: undefined,
          declaredFormat: undefined,
          detectedFormat: undefined,
          retryable: undefined,
          retryCount: (t.retryCount || 0) + 1,
        };
      }
    }

    await this.start();
  }

  /**
   * Remove file from queue
   */
  removeFile(fileId: string): void {
    this.tasks = this.tasks.filter((t) => t.fileId !== fileId);
    this.notifyProgress();
  }

  /**
   * Get queue state
   */
  getState(): UploadQueueState {
    const totalFiles = this.tasks.length;
    const uploadedFiles = this.tasks.filter(
      (t) => t.status === 'success' || t.status === 'skipped',
    ).length;
    const successCount = this.tasks.filter(
      (t) => t.status === 'success',
    ).length;
    const skippedCount = this.tasks.filter(
      (t) => t.status === 'skipped',
    ).length;
    const failedCount = this.tasks.filter(
      (t) => t.status === 'error' || t.status === 'rejected',
    ).length;
    const totalBytes = this.tasks.reduce((sum, t) => sum + t.totalBytes, 0);
    const uploadedBytes = this.tasks.reduce(
      (sum, t) => sum + t.uploadedBytes,
      0,
    );
    const overallProgress =
      totalFiles > 0 ? (uploadedFiles / totalFiles) * 100 : 0;
    const sizeProgress =
      totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;

    // Calculate average speed (only for uploading tasks)
    const uploadingTasks = this.tasks.filter((t) => t.status === 'uploading');
    const averageSpeed =
      uploadingTasks.length > 0
        ? uploadingTasks.reduce((sum, t) => sum + t.speed, 0) /
          uploadingTasks.length
        : 0;

    const isUploading = this.tasks.some((t) => t.status === 'uploading');

    return {
      tasks: this.tasks,
      totalFiles,
      uploadedFiles,
      totalBytes,
      uploadedBytes,
      overallProgress,
      sizeProgress,
      averageSpeed,
      isUploading,
      successCount,
      skippedCount,
      failedCount,
    };
  }

  /**
   * Notify progress callback
   */
  private notifyProgress(): void {
    if (this.onProgressCallback) {
      this.onProgressCallback(this.getState());
    }
  }

  /**
   * Generate unique file ID
   */
  private generateFileId(): string {
    return `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Generate unique group ID
   */
  private generateGroupId(): string {
    return `group_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get auth token from localStorage (same as authorization-util.ts)
   */
  private getAuthToken(): string | null {
    // Get from localStorage (same key as used in request.ts)
    // The Authorization value already includes 'Bearer ' prefix
    const authorization = localStorage.getItem('Authorization');
    return authorization;
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(error?: string): boolean {
    if (!error) return false;

    const retryableErrors = [
      'network error',
      'timeout',
      'connection refused',
      'econnrefused',
      'econnreset',
      'etimedout',
    ];

    const lowerError = error.toLowerCase();
    return retryableErrors.some((err) => lowerError.includes(err));
  }

  /**
   * Sleep helper
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }
}
